import React, { useMemo, useState } from 'react';
import { AlertTriangle, FileSpreadsheet } from 'lucide-react';
import { resolveTimberCsvPreviewDisplay } from '../../utils/proposalCSVParser';
import {
  buildTimberSupplierCsvPreviewTable,
  type TimberSupplierExportOptions,
} from '../../utils/supplierTimberExport';
import type { ProposalNodeSet } from '../../types';
import './TimberCsvPreviewTable.css';

export interface TimberCsvPreviewTableProps {
  sourceFileName: string;
  csvDelimiterLabel?: string;
  totalDataRows?: number;
  parsedItemCount?: number;
  previewRows?: string[][];
  parseErrors?: string[];
  canPublish: boolean;
  /**
   * Exact CSV text that will be attached to the email. When set, the table is
   * that file — header and cells — rather than a reconstructed timber list.
   */
  csvText?: string;
  /** When set, preview matches supplier email CSV attachment (Structure/Grade/Dryness columns). */
  supplierExport?: {
    nodeSets: ProposalNodeSet[];
    exportOptions?: TimberSupplierExportOptions;
  };
}

function parseCsvRow(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i]!;
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === ',' && !inQuotes) {
      cells.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  cells.push(current);
  return cells;
}

function parseAttachmentCsv(csvText: string): { columns: string[]; rows: string[][] } {
  const lines = csvText.split(/\r?\n/).filter(line => line.length > 0);
  if (!lines.length) return { columns: [], rows: [] };
  const parsed = lines.map(parseCsvRow);
  return { columns: parsed[0] ?? [], rows: parsed.slice(1) };
}

const TimberCsvPreviewTable: React.FC<TimberCsvPreviewTableProps> = ({
  sourceFileName,
  csvDelimiterLabel = '—',
  totalDataRows = 0,
  parsedItemCount = 0,
  previewRows = [],
  parseErrors = [],
  canPublish,
  csvText,
  supplierExport,
}) => {
  const [warningsOpen, setWarningsOpen] = useState(false);

  const attachmentTable = useMemo(
    () => (csvText != null ? parseAttachmentCsv(csvText) : null),
    [csvText],
  );

  const supplierTable = useMemo(() => {
    if (attachmentTable || !supplierExport) return null;
    return buildTimberSupplierCsvPreviewTable(
      { nodeSets: supplierExport.nodeSets },
      supplierExport.exportOptions,
    );
  }, [attachmentTable, supplierExport]);

  const cadworkDisplay = useMemo(
    () => (supplierExport ? null : resolveTimberCsvPreviewDisplay(previewRows)),
    [supplierExport, previewRows],
  );

  const columns = useMemo(() => {
    const labels = attachmentTable?.columns ?? supplierTable?.columns;
    if (labels) {
      return labels.map(label => ({
        label,
        align: label === 'Quantity' || label === 'Board Feet' ? ('right' as const) : ('left' as const),
        kind: label === 'Quantity' || label === 'Board Feet' ? ('number' as const) : ('text' as const),
      }));
    }
    return cadworkDisplay?.columns ?? [];
  }, [attachmentTable, supplierTable, cadworkDisplay]);

  const displayRows = attachmentTable?.rows ?? supplierTable?.rows ?? cadworkDisplay?.rows ?? [];

  const colCount = useMemo(
    () => Math.max(...displayRows.map((r) => r.length), columns.length, 1),
    [displayRows, columns],
  );

  const rowTotal = attachmentTable ? attachmentTable.rows.length : supplierTable?.totalRows ?? totalDataRows;
  const lineCount = attachmentTable ? attachmentTable.rows.length : supplierTable?.totalRows ?? parsedItemCount;
  const truncated = !attachmentTable && displayRows.length < rowTotal;

  return (
    <div className="tcpt">
      <div className="tcpt-head">
        <div className="tcpt-file">
          <FileSpreadsheet size={18} aria-hidden className="tcpt-file-icon" />
          <div>
            <h3 className="tcpt-file-name">{sourceFileName}</h3>
            <p className="tcpt-file-sub">
              {attachmentTable
                ? 'CSV attached to the email'
                : supplierExport
                  ? 'Supplier CSV attachment preview'
                  : 'Timber list preview before publish'}
            </p>
          </div>
        </div>
        <dl className="tcpt-stats">
          <div className="tcpt-stat">
            <dt>Rows</dt>
            <dd>{rowTotal.toLocaleString()}</dd>
          </div>
          <div className="tcpt-stat">
            <dt>{supplierExport ? 'Lines' : 'Parsed lines'}</dt>
            <dd>{lineCount.toLocaleString()}</dd>
          </div>
          {!supplierExport && (
            <div className="tcpt-stat">
              <dt>Delimiter</dt>
              <dd>{csvDelimiterLabel}</dd>
            </div>
          )}
          {truncated && (
            <div className="tcpt-stat tcpt-stat--muted">
              <dt>Showing</dt>
              <dd>First {displayRows.length}</dd>
            </div>
          )}
        </dl>
      </div>

      {!canPublish && !attachmentTable && (
        <p className="tcpt-banner tcpt-banner--error" role="alert">
          This file does not produce any valid timber lines. Fix the CSV in Project Hub, then try again.
        </p>
      )}

      {parseErrors.length > 0 && canPublish && !supplierExport && (
        <div className="tcpt-warnings">
          <button
            type="button"
            className="tcpt-warnings-toggle"
            onClick={() => setWarningsOpen((v) => !v)}
            aria-expanded={warningsOpen}
          >
            <AlertTriangle size={15} aria-hidden />
            {parseErrors.length} row{parseErrors.length === 1 ? '' : 's'} skipped during parse
            <span className="tcpt-warnings-chevron" aria-hidden>{warningsOpen ? '▾' : '▸'}</span>
          </button>
          {warningsOpen && (
            <ul className="tcpt-warnings-list">
              {parseErrors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="tcpt-table-wrap">
        <table className="tcpt-table">
          <thead>
            <tr>
              {columns.map((col, ci) => (
                <th
                  key={ci}
                  scope="col"
                  className={[
                    'tcpt-th',
                    `tcpt-th--${col.align}`,
                    `tcpt-th--${col.kind}`,
                    col.label === 'Piece' || col.label === 'Name' ? 'tcpt-th--piece' : '',
                    col.label === 'Material' ? 'tcpt-th--material' : '',
                    col.label === 'Species' ? 'tcpt-th--species' : '',
                    col.label === 'Grade' ? 'tcpt-th--grade' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  title={col.label}
                >
                  {col.label}
                </th>
              ))}
              {columns.length < colCount &&
                Array.from({ length: colCount - columns.length }, (_, i) => (
                  <th key={`extra-${i}`} scope="col" className="tcpt-th">
                    Col {columns.length + i + 1}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((cells, ri) => {
              const padded = [...cells];
              while (padded.length < colCount) padded.push('');
              return (
                <tr key={ri}>
                  {padded.map((value, ci) => {
                    const col = columns[ci];
                    const align = col?.align ?? 'left';
                    const kind = col?.kind ?? 'text';
                    const empty = !value.trim();
                    const isPiece = col?.label === 'Piece' || col?.label === 'Name';
                    const isMaterial = col?.label === 'Material';
                    const isSpecies = col?.label === 'Species';
                    const isGrade = col?.label === 'Grade';
                    return (
                      <td
                        key={ci}
                        className={[
                          'tcpt-td',
                          `tcpt-td--${align}`,
                          `tcpt-td--${kind}`,
                          isPiece ? 'tcpt-td--piece' : '',
                          isMaterial ? 'tcpt-td--material' : '',
                          isSpecies ? 'tcpt-td--species' : '',
                          isGrade ? 'tcpt-td--grade' : '',
                          empty ? 'tcpt-td--empty' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        title={value || undefined}
                      >
                        {empty ? '—' : value}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TimberCsvPreviewTable;
